import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, cents, month, points } from "../../lib";
import { ErrorBox, Loading } from "../../app/ui";
import {
  Checklist,
  GrantCopy,
  PartnerPage,
  ruleText,
  STATUS_LABEL,
} from "./common";
import type { Dashboard, RulesState } from "./types";

const BILL_STATUS: Record<string, string> = {
  draft: "Preparing",
  sent: "Due",
  paid: "Paid",
  overdue: "Overdue",
  void: "Void",
  zero: "Nothing due",
};

export function DashboardPage() {
  return <PartnerPage>{(pid) => <DashboardBody pid={pid} />}</PartnerPage>;
}

function DashboardBody({ pid }: { pid: string }) {
  const q = useQuery({
    queryKey: ["partners", "dashboard", pid],
    queryFn: () => api<Dashboard>("GET", `/api/partners/${pid}/dashboard`),
  });
  const r = useQuery({
    queryKey: ["partners", "rules", pid],
    queryFn: () => api<RulesState>("GET", `/api/partners/${pid}/rules`),
  });
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading />;
  const d = q.data;
  const setup =
    d.partner.status === "draft" || d.partner.status === "pending_review";
  const open = d.checklist.filter((c) => c.due_now && !c.done);
  const rules = r.data?.current ?? r.data?.upcoming;
  const button = d.checklist.find((c) => c.item === "website_button");
  return (
    <div className="stack" data-testid="partners-dashboard">
      <div className="row">
        <h1>{d.partner.name}</h1>
        <span className="tag" data-testid="partners-status">
          {STATUS_LABEL[d.partner.status] ?? d.partner.status}
        </span>
      </div>

      {setup ? (
        <section className="panel stack">
          <h2>Get set up</h2>
          <Checklist items={d.checklist} />
          {d.partner.status === "draft" && open.length === 0 && (
            <Link
              className="button primary"
              to="/partner/review"
              data-testid="partners-go-review"
            >
              Send for review
            </Link>
          )}
          {d.partner.status === "pending_review" && (
            <p className="muted">Thanks. We'll switch you on soon.</p>
          )}
        </section>
      ) : (
        <div className="grid">
          <section className="panel">
            <h2>Customers earning</h2>
            <span className="big num" data-testid="partners-customers-linked">
              {points(d.numbers.customers_linked)}
            </span>
          </section>
          <section className="panel">
            <h2>Points waiting on your bill</h2>
            <span className="big num" data-testid="partners-points-waiting">
              {points(d.numbers.points_waiting)}
            </span>
          </section>
          <section className="panel">
            <h2>Due now</h2>
            <span className="big num" data-testid="partners-open-bills">
              {cents(d.numbers.open_bills_cents)}
            </span>
          </section>
        </div>
      )}

      {d.numbers.grant.cap_cents > 0 && (
        <section className="panel">
          <GrantCopy grant={d.numbers.grant} />
        </section>
      )}

      {(rules || !setup) && (
        <section className="panel stack">
          <h2>Your points</h2>
          {rules ? (
            <ul data-testid="partners-rules-in-force">
              {rules.rows.map((x) => (
                <li key={x.from}>{ruleText(x, rules.rows.length === 1)}</li>
              ))}
            </ul>
          ) : (
            <p className="muted">Not set yet.</p>
          )}
          <Link to="/partner/rules">
            {rules ? "Change" : "Set your points"}
          </Link>
        </section>
      )}

      {!setup && (
        <section className="panel stack">
          <h2>Bills</h2>
          {d.bills.length === 0 ? <p className="muted">No bills yet.</p> : null}
          {d.bills.map((b) => (
            <div className="row" key={b.run_id}>
              <span>{month(b.period)}</span>
              <span className="num">{cents(b.amount_cents)}</span>
              <span className="tag">{BILL_STATUS[b.status] ?? b.status}</span>
            </div>
          ))}
          <Link to="/partner/bills">All bills</Link>
        </section>
      )}

      {d.partner.status === "live" && button && !button.done && (
        <section className="panel stack" data-testid="partners-home-button">
          <h2>Website button</h2>
          <img src="/badge/light.svg" alt="Earn rewards with ernd" style={{ alignSelf: "flex-start" }} />
          <Link to="/partner/button">Get the code</Link>
        </section>
      )}
    </div>
  );
}
